import React from "react";
import {
  User,
  Project,
  Task,
  WorkLogEntry,
  Material,
} from "../../types";
import { ViewType } from "../../types/navigation";
import { DashboardStats } from "../dashboard/DashboardStats";
import { ProjectOverview } from "../dashboard/ProjectOverview";
import { TaskList } from "../dashboard/TaskList";
import { ProjectsGrid } from "../projects/ProjectsGrid";
import { TaskManager } from "../tasks/TaskManager";
import { UserManagement } from "../users/UserManagement";
import { MaterialsManager } from "../materials/MaterialsManager";
import { ReportsManager } from "../reports/ReportsManager";
import { AdminSettingsPage } from "../settings/AdminSettingsPage";
import { TeamOverview } from "../team/TeamOverview";
import { WorkLogManager } from "../worklog/WorkLogManager";
import { RevenueOverview } from "../revenue/RevenueOverview";
import { ProjectArchives } from "../archives/ProjectArchives";
import { ClientDashboard } from "../client/ClientDashboard";
import { ClientProjectStatus } from "../client/ClientProjectStatus";
import { ClientDocumentation } from "../client/ClientDocumentation";
import { ClientBilling } from "../revenue/ClientBilling";
import { ActivityLogs } from "../logs/ActivityLogs";
import { FeatureBank } from "../features/FeatureBank";
import { LayoutDashboard } from "lucide-react";

interface AppRouterProps {
  currentUser: User;
  currentView: ViewType;
  projects: Project[];
  tasks: Task[];
  users: User[];
  workLogs: WorkLogEntry[];
  materials: Material[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  onCreateProject?: (projectData: Omit<Project, "id">) => Promise<Project>;
  onUpdateProject: (project: Project) => void;
  onDeleteProject: (projectId: string) => void;
  onAssignFabricator?: (
    projectId: string,
    fabricatorId: string,
    message?: string
  ) => void;
  onBroadcastFabricators?: (projectId: string, message?: string) => void;
  onAcceptAssignment: (
    assignmentId: string,
    response?: string,
    projectId?: string
  ) => void;
  onDeclineAssignment: (
    assignmentId: string,
    response?: string,
    projectId?: string
  ) => void;
  onCreateTask: (taskData: Omit<Task, "id" | "createdAt" | "updatedAt">) => void;
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onUpdateTaskStatus: (taskId: string, status: Task["status"]) => void;
  onDeleteTask: (taskId: string) => void;
  onAddWorkLog: (
    workLogData: Omit<WorkLogEntry, "id" | "createdAt">
  ) => Promise<void>;
  onUpdateWorkLog: (id: string, updates: Partial<WorkLogEntry>) => Promise<void>;
  onDeleteWorkLog: (id: string) => Promise<void>;
  onAddMaterial: (
    materialData: Omit<Material, "id" | "addedAt">
  ) => Promise<void>;
  onUpdateMaterial: (id: string, updates: Partial<Material>) => Promise<void>;
  onDeleteMaterial: (id: string) => Promise<void>;
  onAddUser: (user: User) => void;
  onUpdateCurrentUser: (user: User) => void;
}

export const AppRouter: React.FC<AppRouterProps> = ({
  currentUser,
  currentView,
  projects,
  tasks,
  users,
  workLogs,
  materials,
  setUsers,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
  onAssignFabricator,
  onBroadcastFabricators,
  onAcceptAssignment,
  onDeclineAssignment,
  onCreateTask,
  onUpdateTask,
  onUpdateTaskStatus,
  onDeleteTask,
  onAddWorkLog,
  onUpdateWorkLog,
  onDeleteWorkLog,
  onAddMaterial,
  onUpdateMaterial,
  onDeleteMaterial,
  onAddUser,
  onUpdateCurrentUser,
}) => {
  // Client role view rendering
  if (currentUser.role === "client") {
    switch (currentView) {
      case "dashboard":
        return (
          <ClientDashboard
            currentUser={currentUser}
            projects={projects}
            users={users}
            workLogs={workLogs}
            tasks={tasks}
          />
        );
      case "project-status":
        return (
          <ClientProjectStatus
            currentUser={currentUser}
            projects={projects}
            users={users}
            workLogs={workLogs}
            tasks={tasks}
          />
        );
      case "documentation":
        return (
          <ClientDocumentation
            currentUser={currentUser}
            projects={projects}
            users={users}
            workLogs={workLogs}
            tasks={tasks}
            onUpdateProject={onUpdateProject}
          />
        );
      case "billing":
        return <ClientBilling />;
      default:
        return (
          <ClientDashboard
            currentUser={currentUser}
            projects={projects}
            users={users}
            workLogs={workLogs}
            tasks={tasks}
          />
        );
    }
  }

  // Internal team role view rendering
  switch (currentView) {
    case "dashboard":
      return (
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              View your latest project activity, task progress, and team summary.
            </p>
          </div>
          <DashboardStats
            projects={projects}
            tasks={tasks}
            users={users}
            currentUser={currentUser}
          />
          <div className="grid gap-6 lg:grid-cols-2">
            <ProjectOverview projects={projects} currentUser={currentUser} />
            <TaskList
              tasks={tasks}
              projects={projects}
              currentUser={currentUser}
              onUpdateTaskStatus={onUpdateTaskStatus}
            />
          </div>
        </div>
      );

    case "projects":
      return (
        <ProjectsGrid
          projects={projects}
          users={users}
          currentUser={currentUser}
          onCreateProject={
            currentUser.role === "admin" || currentUser.role === "supervisor"
              ? onCreateProject
              : undefined
          }
          onAssignFabricator={
            currentUser.role === "supervisor"
              ? onAssignFabricator
              : undefined
          }
          onUpdateProject={onUpdateProject}
          onAcceptAssignment={onAcceptAssignment}
          onDeclineAssignment={onDeclineAssignment}
          onCreateUser={onAddUser}
          onBroadcastFabricators={
            currentUser.role === "supervisor"
              ? onBroadcastFabricators
              : undefined
          }
        />
      );

    case "materials":
      if (currentUser.role === "fabricator") {
        return (
          <MaterialsManager
            currentUser={currentUser}
            projects={projects}
            materials={materials}
            onAddMaterial={onAddMaterial}
            onUpdateMaterial={onUpdateMaterial}
            onDeleteMaterial={onDeleteMaterial}
          />
        );
      }
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Materials management is only available for fabricators.
            </p>
          </div>
        </div>
      );

    case "team":
      if (currentUser.role === "admin" || currentUser.role === "supervisor") {
        return (
          <TeamOverview
            users={users}
            projects={projects}
            tasks={tasks}
            workLogs={workLogs}
            currentUser={currentUser}
            onAssignFabricator={onAssignFabricator}
          />
        );
      }
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Team overview is only available for admins and supervisors.
            </p>
          </div>
        </div>
      );

    case "activity-logs":
      if (currentUser.role === "admin") {
        return <ActivityLogs />;
      }
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Activity logs are only available for administrators.
            </p>
          </div>
        </div>
      );

    case "feature-bank":
      return <FeatureBank currentUser={currentUser} />;

    case "assignments":
      if (currentUser.role === "fabricator") {
        if (typeof window !== "undefined") {
          window.location.hash = "projects";
        }
        return (
          <ProjectsGrid
            projects={projects}
            users={users}
            currentUser={currentUser}
            onCreateProject={undefined}
            onAssignFabricator={undefined}
            onUpdateProject={onUpdateProject}
            onAcceptAssignment={onAcceptAssignment}
            onDeclineAssignment={onDeclineAssignment}
            onCreateUser={onAddUser}
          />
        );
      }
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Project assignments are only available for fabricators.
            </p>
          </div>
        </div>
      );

    case "worklog":
      if (currentUser.role === "fabricator") {
        return (
          <WorkLogManager
            currentUser={currentUser}
            projects={projects}
            workLogs={workLogs}
            materials={materials}
            onAddWorkLog={onAddWorkLog}
            onUpdateWorkLog={onUpdateWorkLog}
            onDeleteWorkLog={onDeleteWorkLog}
            onUpdateProject={onUpdateProject}
          />
        );
      }
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Work logs are only available for fabricators.
            </p>
          </div>
        </div>
      );

    case "revenue":
      return (
        <RevenueOverview
          projects={projects}
          materials={materials}
          currentUser={currentUser}
          users={users}
          onUpdateProject={onUpdateProject}
        />
      );

    case "reports":
      return (
        <ReportsManager
          projects={projects}
          users={users}
          tasks={tasks}
          currentUser={currentUser}
        />
      );

    case "archives":
      if (currentUser.role === "admin" || currentUser.role === "supervisor") {
        return (
          <ProjectArchives
            projects={projects}
            users={users}
            materials={materials}
            workLogs={workLogs}
            currentUser={currentUser}
            onUpdateProject={onUpdateProject}
            onDeleteProject={onDeleteProject}
            onCreateProject={onCreateProject}
          />
        );
      }
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Archives are only available for admins and supervisors.
            </p>
          </div>
        </div>
      );

    case "users":
      return (
        <UserManagement
          users={users}
          setUsers={setUsers}
          currentUser={currentUser}
        />
      );

    case "settings":
      if (currentUser.role === "admin" || currentUser.role === "fabricator") {
        return (
          <AdminSettingsPage
            currentUser={currentUser}
            onUpdateCurrentUser={onUpdateCurrentUser}
          />
        );
      }
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Settings are only available for system administrators.
            </p>
          </div>
        </div>
      );

    case "tasks":
      return (
        <TaskManager
          tasks={tasks}
          projects={projects}
          users={users}
          currentUser={currentUser}
          onCreateTask={onCreateTask}
          onUpdateTask={onUpdateTask}
          onDeleteTask={onDeleteTask}
        />
      );

    case "admin-projects":
    case "admin-tasks":
      return (
        <ProjectsGrid
          projects={projects}
          users={users}
          currentUser={currentUser}
          onCreateProject={
            currentUser.role === "admin" || currentUser.role === "supervisor"
              ? onCreateProject
              : undefined
          }
          onAssignFabricator={
            currentUser.role === "supervisor"
              ? onAssignFabricator
              : undefined
          }
          onUpdateProject={onUpdateProject}
          onCreateUser={onAddUser}
        />
      );

    case "billing":
      if (currentUser.role === "admin") {
        return <ClientBilling />;
      }
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Billing is only available for administrators.
            </p>
          </div>
        </div>
      );

    default:
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg mb-2">Feature Coming Soon</h3>
            <p className="text-muted-foreground">
              The {currentView} section is under development.
            </p>
          </div>
        </div>
      );
  }
};
